/**
 * resolveApplyCompetition — the pure precedence at the heart of `/apply`
 * competition scoping (apply-season-auto-derivation spec, task 2.1).
 *
 * Extracted out of `ApplyHost.useResolvedCompetitionId` so the
 * applicant-ignore / director-honor rule is ONE testable function that cannot
 * drift between call sites. It takes no I/O, reads no clock, and mutates
 * nothing — it is a total function of its three inputs.
 *
 * Contract (design "Components and Interfaces #2"):
 *   - Director + explicit competition param present → the explicit competition
 *     (a Director may deep-link a specific competition — R8.3).
 *   - Otherwise (an Applicant, or no explicit param) → the derived competition,
 *     IGNORING any applicant-supplied competition value (R5.5).
 *   - `derivedCompetitionId` is `null` when Season_Derivation is unresolved; the
 *     caller then defers to the `IntakeWindowGate` redirect (R4.2 / R4.3), so
 *     this function propagates that `null` through unchanged.
 *
 * An `explicitCompetitionId` is treated as "present" only when it is a
 * non-blank string, mirroring the original `ApplyHost` guard
 * (`explicit != null && explicit.trim() !== ''`) so an empty/whitespace param
 * never counts as a director deep-link.
 *
 * Validates: Requirements 5.5, 8.3
 */

export interface ResolveApplyCompetitionInput {
  /** Whether the viewer holds explicit-competition (Director) authority. */
  isDirector: boolean;
  /** The raw `?competitionId=` query param value, or `null` when absent. */
  explicitCompetitionId: string | null;
  /**
   * The competition id derived from the Derived_Application_Season, or `null`
   * when Season_Derivation is unresolved.
   */
  derivedCompetitionId: string | null;
}

/** Whether an explicit competition param is present (a non-blank string). */
function hasExplicit(explicitCompetitionId: string | null): explicitCompetitionId is string {
  return explicitCompetitionId != null && explicitCompetitionId.trim() !== '';
}

/**
 * The competition id the Application_Surface scopes to, or `null` when
 * derivation is unresolved and no director deep-link applies.
 */
export function resolveApplyCompetition(input: ResolveApplyCompetitionInput): string | null {
  const { isDirector, explicitCompetitionId, derivedCompetitionId } = input;

  // Director deep-link wins: honor an explicit competition param (R8.3).
  if (isDirector && hasExplicit(explicitCompetitionId)) {
    return explicitCompetitionId;
  }

  // Applicant (or a director without an explicit param): use the derived
  // competition and ignore any applicant-supplied value (R5.5). `null`
  // propagates when derivation is unresolved (R4.2 / R4.3).
  return derivedCompetitionId;
}

export default resolveApplyCompetition;
